import { Ban, Trash } from "lucide-react";
import { useState } from "react";
import { confirmDialog } from "../../../shared/ui/ConfirmHost";
import { notify } from "../lib/notify";
import { useStore } from "../store";
import type { SubTask, Task } from "../types";
import { CancelItemDialog } from "./CancelItemDialog";
import { MoreMenu } from "./ui/more-menu";

/**
 * Убрать видео из публикации.
 *
 * Несданное удаляется совсем: по нему ничего нет, и в истории ему делать
 * нечего. Сданное снимается — версии, модерация и, может быть, файл в
 * Telegram у него уже есть, поэтому строка остаётся, а из счёта уходит.
 * Кому какая кнопка положена, решает вызывающий; сервер проверит ещё раз.
 *
 * `variant="menu"` — то же за «⋯»: рядом с «Отозвать с модерации» отдельная
 * «Снять» нажималась промахом (02.11.2026).
 */
export function VideoRemove({ task, item, variant = "inline" }: { task: Task; item: SubTask; variant?: "inline" | "menu" }) {
  const removeItem = useStore((s) => s.removeItem);
  const [cancelOpen, setCancelOpen] = useState(false);
  if (item.status === "cancelled") return null;

  const sent = item.versions.length > 0;
  const remove = async () => {
    if (!(await confirmDialog({ title: `Удалить «${item.title}» из публикации?`, description: "По видео ещё ничего не сдано — оно удалится совсем.", confirmText: "Удалить", destructive: true }))) return;
    removeItem(task.id, item.id);
    notify(`«${item.title}» удалено из публикации`, { icon: "info" });
  };
  const dialog = <CancelItemDialog task={task} item={item} open={cancelOpen} onClose={() => setCancelOpen(false)} />;

  if (variant === "menu") {
    return (
      <>
        <MoreMenu
          items={[
            sent
              ? { icon: <Ban />, label: "Снять ролик", hint: "Не выйдет — с причиной", destructive: true, onSelect: () => setCancelOpen(true) }
              : { icon: <Trash />, label: "Удалить из публикации", destructive: true, onSelect: () => void remove() },
          ]}
        />
        {dialog}
      </>
    );
  }

  if (!sent) {
    return (
      <button
        type="button"
        title="Удалить видео из публикации"
        onClick={(e) => {
          e.stopPropagation();
          void remove();
        }}
        className="grid size-7 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-rework-fg"
      >
        <Trash className="size-[15px]" />
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setCancelOpen(true);
        }}
        className="shrink-0 text-xs font-medium text-muted-foreground underline underline-offset-2 hover:text-rework-fg"
      >
        Снять
      </button>
      {dialog}
    </>
  );
}
